/**
 * HudUI.ts — In-game HUD overlay
 *
 * Manages the function input, scoreboard, turn indicator,
 * error messages, turn history panel, and game-over screens.
 * Supports arrow-key cycling through previous function inputs.
 */

export interface HudCallbacks {
  onSubmitFunction: (expression: string, force?: boolean) => void;
  onNewGame: () => void;
  onBackToMenu: () => void;
  onToggleTraces?: (enabled: boolean) => void;
  onWatchReplay?: () => void;
  onChatSend?: (msg: string) => void;
  onCopyReplay?: () => void;
}

/** A single entry in the turn history */
export interface HistoryEntry {
  teamIndex: number;
  soldierLabel: string;
  expression: string;
  hits: number;
}

export class HudUI {
  private container: HTMLDivElement | null = null;
  private inputEl: HTMLInputElement | null = null;
  private fireBtn: HTMLButtonElement | null = null;
  private errorEl: HTMLDivElement | null = null;
  private turnIndicatorEl: HTMLDivElement | null = null;
  private team1ScoreEl: HTMLSpanElement | null = null;
  private team2ScoreEl: HTMLSpanElement | null = null;
  private botThinkingEl: HTMLDivElement | null = null;
  private historyListEl: HTMLDivElement | null = null;
  private timerEl: HTMLDivElement | null = null;
  private resultOverlay: HTMLDivElement | null = null;
  private callbacks: HudCallbacks | null = null;

  /** History of player-typed expressions for arrow-key recall */
  private inputHistory: string[] = [];
  private historyPointer: number = -1;
  private chatInputEl: HTMLInputElement | null = null;
  private chatBtnEl: HTMLButtonElement | null = null;
  private savedInput: string = '';

  /**
   * Creates and displays the HUD.
   */
  show(callbacks: HudCallbacks): void {
    this.destroy();
    this.callbacks = callbacks;
    this.inputHistory = [];
    this.historyPointer = -1;

    const container = document.createElement('div');
    container.className = 'hud-container';
    container.id = 'game-hud';

    container.innerHTML = `
      <div class="hud-topbar">
        <div class="hud-score">
          <div class="hud-team hud-team--blue">
            <span class="hud-team-dot"></span>
            <span>Team 1: <strong id="hud-score-t1">2</strong></span>
          </div>
          <span class="hud-vs">vs</span>
          <div class="hud-team hud-team--red">
            <span class="hud-team-dot"></span>
            <span>Team 2: <strong id="hud-score-t2">2</strong></span>
          </div>
        </div>
        <div class="hud-turn-indicator hud-turn-indicator--player" id="hud-turn">
          Your turn
        </div>
        <div class="hud-timer hud-timer--hidden" id="hud-timer">
          ⏱ 01:00
        </div>
      </div>

      <div class="hud-history hud-history--hidden" id="hud-history" style="display: flex; flex-direction: column;">
        <div class="hud-history-header">
          <span class="hud-history-title">Chat</span>
          <button class="hud-history-close-btn" id="hud-history-close">✕</button>
        </div>
        <div class="hud-history-list" id="hud-history-list" style="flex: 1;"></div>
        <div class="hud-chat-input-row" style="padding: 8px; border-top: 1px solid var(--color-border); display: flex; gap: 8px; align-items: center; background: var(--bg-surface);">
          <input type="text" id="hud-chat-input" placeholder="Type a message..." style="flex: 1; padding: 6px; border: 1px solid var(--color-border); border-radius: 4px; font-family: var(--font-ui); font-size: 12px; background: transparent; color: var(--color-text); outline: none;" />
          <button id="hud-chat-btn" style="padding: 6px 12px; background: var(--color-team1); color: #fff; border: none; border-radius: 4px; font-family: var(--font-ui); font-size: 12px; font-weight: 500; cursor: pointer;">Send</button>
        </div>
      </div>

      <div class="hud-bottombar">
        <div class="hud-error" id="hud-error" style="display: none;"></div>
        <div class="hud-bot-thinking" id="hud-bot-thinking" style="display: none;">
          Bot thinking
          <div class="hud-bot-thinking-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
        <div class="hud-input-row" id="hud-input-row">
          <span class="hud-fx-label">f(x) =</span>
          <input
            type="text"
            class="hud-function-input"
            id="hud-function-input"
            placeholder="e.g. x^2 + sin(x)"
            autocomplete="off"
            spellcheck="false"
          />
          <button class="hud-fire-btn" id="hud-fire-btn">Fire</button>
        </div>
      </div>

      <div class="hud-actions">
        <button class="hud-action-btn" id="hud-btn-menu">← Menu</button>
        <button class="hud-action-btn" id="hud-btn-new">New game</button>
        <button class="hud-action-btn" id="hud-btn-history">History</button>
      </div>
    `;

    document.body.appendChild(container);
    this.container = container;

    // Cache elements
    this.inputEl = container.querySelector('#hud-function-input') as HTMLInputElement;
    this.fireBtn = container.querySelector('#hud-fire-btn') as HTMLButtonElement;
    this.errorEl = container.querySelector('#hud-error') as HTMLDivElement;
    this.turnIndicatorEl = container.querySelector('#hud-turn') as HTMLDivElement;
    this.team1ScoreEl = container.querySelector('#hud-score-t1') as HTMLSpanElement;
    this.team2ScoreEl = container.querySelector('#hud-score-t2') as HTMLSpanElement;
    this.botThinkingEl = container.querySelector('#hud-bot-thinking') as HTMLDivElement;
    this.historyListEl = container.querySelector('#hud-history-list') as HTMLDivElement;
    this.timerEl = container.querySelector('#hud-timer') as HTMLDivElement;

    const historyPanel = container.querySelector('#hud-history') as HTMLDivElement;
    const historyBtn = container.querySelector('#hud-btn-history') as HTMLButtonElement;
    const historyCloseBtn = container.querySelector('#hud-history-close') as HTMLButtonElement;
    this.chatInputEl = container.querySelector('#hud-chat-input') as HTMLInputElement;
    this.chatBtnEl = container.querySelector('#hud-chat-btn') as HTMLButtonElement;

    const toggleHistory = () => {
      historyPanel.classList.toggle('hud-history--hidden');
    };

    historyBtn.addEventListener('click', toggleHistory);
    historyCloseBtn.addEventListener('click', () => historyPanel.classList.add('hud-history--hidden'));

    // Event listeners
    const handleChatSend = () => {
      if (!this.chatInputEl || !this.callbacks || !this.callbacks.onChatSend) return;
      const val = this.chatInputEl.value.trim();
      if (val) {
        if (!localStorage.getItem('mathillery_chat_rules_accepted')) {
          this.showChatRulesModal(() => {
            if (this.callbacks?.onChatSend) {
              this.callbacks.onChatSend(val);
              if (this.chatInputEl) this.chatInputEl.value = '';
            }
          });
          return;
        }

        this.callbacks.onChatSend(val);
        this.chatInputEl.value = '';
      }
    };

    if (this.chatBtnEl) this.chatBtnEl.addEventListener('click', handleChatSend);
    if (this.chatInputEl) {
      this.chatInputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleChatSend();
        }
        e.stopPropagation(); // don't trigger phaser keys or fx history
      });
    }

    this.fireBtn.addEventListener('click', () => this.handleSubmit());
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.handleSubmit();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.navigateHistory(-1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.navigateHistory(1);
      }
    });

    const menuBtn = container.querySelector('#hud-btn-menu') as HTMLButtonElement;
    const newBtn = container.querySelector('#hud-btn-new') as HTMLButtonElement;

    menuBtn.addEventListener('click', () => callbacks.onBackToMenu());
    newBtn.addEventListener('click', () => callbacks.onNewGame());

    // Focus the input
    this.inputEl.focus();
  }

  private handleSubmit(force: boolean = false): void {
    if (!this.inputEl || !this.callbacks) return;
    const value = this.inputEl.value.trim();
    if (!value) return;

    // Add to input history (avoid duplicating the last entry)
    if (this.inputHistory.length === 0 || this.inputHistory[this.inputHistory.length - 1] !== value) {
      this.inputHistory.push(value);
    }
    this.historyPointer = -1;
    this.savedInput = '';

    this.callbacks.onSubmitFunction(value, force);
  }

  /**
   * Navigate through input history with arrow keys.
   * direction: -1 = older (ArrowUp), +1 = newer (ArrowDown)
   */
  private navigateHistory(direction: number): void {
    if (!this.inputEl || this.inputHistory.length === 0) return;

    if (this.historyPointer === -1) {
      // First press — save current input and start from the end
      if (direction === -1) {
        this.savedInput = this.inputEl.value;
        this.historyPointer = this.inputHistory.length - 1;
      } else {
        return; // Already at the newest
      }
    } else {
      const next = this.historyPointer + direction;
      if (next < 0) return; // Can't go further back
      if (next >= this.inputHistory.length) {
        // Back to the saved (current) input
        this.historyPointer = -1;
        this.inputEl.value = this.savedInput;
        return;
      }
      this.historyPointer = next;
    }

    this.inputEl.value = this.inputHistory[this.historyPointer];
  }

  /**
   * Adds an entry to the visual turn history panel.
   */
  addHistoryEntry(entry: HistoryEntry): void {
    if (!this.historyListEl) return;

    const el = document.createElement('div');
    el.className = `hud-history-entry hud-history-entry--team${entry.teamIndex}`;

    const resultClass = entry.hits > 0 ? 'hud-history-result--hit' : 'hud-history-result--miss';
    const resultText = entry.hits > 0 ? `${entry.hits} hit${entry.hits > 1 ? 's' : ''}` : 'miss';

    el.style.opacity = '0.8'; 
    el.innerHTML = `
      <span class="hud-history-who" style="font-size: 0.7rem;">[LOG] ${entry.soldierLabel}</span>
      <span class="hud-history-fn" style="color: var(--color-text-muted);" title="${entry.expression}">${entry.expression}</span>
      <span class="hud-history-result ${resultClass}">${resultText}</span>
    `;

    this.historyListEl.appendChild(el);
    // Auto-scroll to bottom
    this.historyListEl.scrollTop = this.historyListEl.scrollHeight;
  }

  /**
   * Adds a chat message to the visual turn history panel.
   */
  addChatEntry(sender: string, message: string, isSelf: boolean = false): void {
    if (!this.historyListEl) return;

    const el = document.createElement('div');
    
    // Base styles for the message container
    el.style.display = 'flex';
    el.style.flexDirection = 'column';
    el.style.alignItems = isSelf ? 'flex-end' : 'flex-start';
    el.style.margin = '4px 8px 8px 8px';
    
    // Bubble styles
    const bgColor = isSelf ? 'var(--color-team1)' : '#E9ECEF';
    const textColor = isSelf ? '#FFFFFF' : 'var(--color-text)';
    const borderRadius = isSelf ? '12px 12px 2px 12px' : '12px 12px 12px 2px';

    el.innerHTML = `
      <span style="font-size: 0.65rem; color: var(--color-text-muted); margin-bottom: 2px; padding: 0 4px;">${sender}</span>
      <div style="background: ${bgColor}; color: ${textColor}; padding: 6px 10px; border-radius: ${borderRadius}; font-family: var(--font-ui); font-size: 0.85rem; max-width: 85%; word-break: break-word; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
        ${message}
      </div>
    `;

    this.historyListEl.appendChild(el);
    this.historyListEl.scrollTop = this.historyListEl.scrollHeight;
  }

  /**
   * Updates the scoreboard.
   */
  updateScore(team1Alive: number, team2Alive: number): void {
    if (this.team1ScoreEl) this.team1ScoreEl.textContent = String(team1Alive);
    if (this.team2ScoreEl) this.team2ScoreEl.textContent = String(team2Alive);
  }

  /**
   * Sets the turn indicator.
   */
  setTurnIndicator(text: string, isPlayer: boolean): void {
    if (!this.turnIndicatorEl) return;
    this.turnIndicatorEl.textContent = text;
    this.turnIndicatorEl.className = `hud-turn-indicator ${isPlayer ? 'hud-turn-indicator--player' : 'hud-turn-indicator--bot'
      }`;
  }

  /**
   * Shows and updates the timer element.
   */
  updateTimer(secondsRemaining: number): void {
    if (!this.timerEl) return;
    this.timerEl.classList.remove('hud-timer--hidden');

    const m = Math.floor(secondsRemaining / 60);
    const s = secondsRemaining % 60;
    const timeStr = `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    this.timerEl.textContent = `⏱ ${timeStr}`;

    if (secondsRemaining <= 10) {
      this.timerEl.style.color = '#ff6b6b';
      this.timerEl.style.animation = 'pulse 1s infinite';
    } else {
      this.timerEl.style.color = '';
      this.timerEl.style.animation = '';
    }
  }

  /**
   * Hides the timer element.
   */
  hideTimer(): void {
    if (!this.timerEl) return;
    this.timerEl.classList.add('hud-timer--hidden');
    this.timerEl.style.color = '';
    this.timerEl.style.animation = '';
  }

  /**
   * Forces a fire action (used when timer runs out).
   */
  forceFire(): void {
    if (!this.inputEl || !this.callbacks) return;
    const val = this.inputEl.value.trim() || '0'; // default to 0 if empty
    this.inputEl.value = val;
    this.handleSubmit(true);
  }

  /**
   * Shows an error message.
   */
  showError(message: string): void {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.style.display = 'block';
    this.errorEl.style.animation = 'none';
    this.errorEl.offsetHeight; // force reflow
    this.errorEl.style.animation = 'shake 0.35s var(--ease)';
  }

  /**
   * Hides the error message.
   */
  hideError(): void {
    if (this.errorEl) this.errorEl.style.display = 'none';
  }

  /**
   * Shows a one-time chat rules disclaimer.
   */
  private showChatRulesModal(onAccept: () => void): void {
    const modal = document.createElement('div');
    modal.className = 'setup-modal-backdrop';
    modal.style.zIndex = '99999';
    modal.innerHTML = `
      <div class="setup-modal-card" style="max-width: 400px;">
        <div class="setup-modal-header" style="border-bottom: 1px solid var(--color-border); padding-bottom: 12px; margin-bottom: 16px;">
          <h2>Chat Rules</h2>
        </div>
        <p style="margin-bottom: 12px; font-size: 0.95rem; line-height: 1.5; color: var(--color-text);">
          Welcome to the chat! Before you send your first message, please agree to the following:
        </p>
        <ul style="margin-bottom: 24px; font-size: 0.9rem; line-height: 1.5; color: var(--color-text-muted); padding-left: 20px;">
          <li style="margin-bottom: 8px;"><strong>Be respectful:</strong> Treat your opponent with kindness and good sportsmanship.</li>
          <li style="margin-bottom: 8px;"><strong>No personal info:</strong> Mathillery P2P is anonymous. Do not share passwords, real names, or addresses.</li>
          <li><strong>Have fun:</strong> Enjoy the math battles!</li>
        </ul>
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button id="chat-rules-cancel" class="setup-btn setup-btn--secondary">Cancel</button>
          <button id="chat-rules-accept" class="setup-btn setup-btn--primary">I Agree</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const acceptBtn = modal.querySelector('#chat-rules-accept');
    const cancelBtn = modal.querySelector('#chat-rules-cancel');

    acceptBtn?.addEventListener('click', () => {
      localStorage.setItem('mathillery_chat_rules_accepted', '1');
      modal.remove();
      onAccept();
    });

    cancelBtn?.addEventListener('click', () => {
      modal.remove();
    });
  }

  /**
   * Enables or disables the input.
   */
  setInputEnabled(enabled: boolean): void {
    if (this.inputEl) {
      this.inputEl.disabled = !enabled;
      if (enabled) {
        this.inputEl.value = '';
        this.inputEl.focus();
      }
    }
    if (this.fireBtn) this.fireBtn.disabled = !enabled;
  }

  /**
   * Shows the bot thinking indicator.
   */
  showBotThinking(show: boolean): void {
    if (this.botThinkingEl) {
      this.botThinkingEl.style.display = show ? 'flex' : 'none';
    }
    const inputRow = this.container?.querySelector('#hud-input-row') as HTMLElement;
    if (inputRow) {
      inputRow.style.display = show ? 'none' : 'flex';
    }
  }

  /**
   * Shows the game result overlay.
   */
  showResult(victory: boolean, teamName: string, killingExpression?: string | null): void {
    if (this.resultOverlay) return;

    let defeatSubtitle = `${teamName} has been eliminated`;
    if (!victory && killingExpression) {
      const eq = `f(x) = ${killingExpression}`;
      const templates = [
        `You could not withstand ${eq}`,
        `Bazooked by ${eq}`,
        `Did you sleep during math class? Slain by ${eq}`,
        `Looks like you're not a "math person"... Deleted by ${eq}`,
        `Maybe stick to addition... Destroyed by ${eq}`,
        `Your math teacher would be so disappointed. Crushed by ${eq}`,
        `A simple ${eq} was too much for you?`,
        `Calculators won't save you from ${eq}`,
        `Skill issue, clearly. Eliminated by ${eq}`,
        `Is ${eq} too advanced for you?`,
        `Outsmarted and outcalculated by ${eq}`,
        `Next time try to plot ${eq} in your head first.`,
        `Sent back to kindergarten by ${eq}`,
        `Your coordinates have been forcibly updated by ${eq}`,
        `You just became a statistical outlier thanks to ${eq}`,
        `Divided by zero? No, just destroyed by ${eq}`,
        `Looks like ${eq} was out of your domain.`,
        `Your probability of winning just hit zero thanks to ${eq}`,
        `Are you sure you passed algebra? Deleted by ${eq}`,
        `That trajectory was perfectly calculated to ruin your day with ${eq}`,
        `You are now a solved equation. Erased by ${eq}`,
        `No extra credit can save you from ${eq}`,
        `Graphing calculators are weeping for you after ${eq}`,
        `Your Cartesian privileges have been permanently revoked by ${eq}`,
        `Error 404: Math skills not found. Terminated by ${eq}`,
        `You got absolutely factored by ${eq}`,
        `Get mathematically styled on by ${eq}`,
        `Reduced to a fraction by ${eq}`,
        `That was so bad... maybe you should just start using AI to write your equations. Crushed by ${eq}`
      ];
      defeatSubtitle = templates[Math.floor(Math.random() * templates.length)];
    }

    const overlay = document.createElement('div');
    overlay.className = 'result-overlay';
    overlay.innerHTML = `
      <h1 class="result-title ${victory ? 'result-title--victory' : 'result-title--defeat'}">
        ${victory ? 'Victory!' : 'Defeat'}
      </h1>
      <p class="result-subtitle">${victory ? `${teamName} wins` : defeatSubtitle}</p>
      <div class="result-actions">
        <button class="result-btn result-btn--primary" id="result-btn-new">New game</button>
        <button class="result-btn" id="result-btn-replay">Copy Replay Code</button>
        <button class="result-btn" id="result-btn-menu">Main menu</button>
      </div>
    `;

    document.body.appendChild(overlay);
    this.resultOverlay = overlay;

    const newBtn = overlay.querySelector('#result-btn-new') as HTMLButtonElement;
    const replayBtn = overlay.querySelector('#result-btn-replay') as HTMLButtonElement;
    const menuBtn = overlay.querySelector('#result-btn-menu') as HTMLButtonElement;

    newBtn.addEventListener('click', () => {
      this.hideResult();
      this.callbacks?.onNewGame();
    });

    replayBtn.addEventListener('click', () => {
      const origText = replayBtn.textContent;
      replayBtn.textContent = 'Copied!';
      setTimeout(() => {
        if (replayBtn) replayBtn.textContent = origText;
      }, 2000);

      if (this.callbacks?.onCopyReplay) {
        this.callbacks.onCopyReplay();
      } else {
        this.showToast('Replay code copied!');
      }
    });

    menuBtn.addEventListener('click', () => {
      this.hideResult();
      this.callbacks?.onBackToMenu();
    });
  }

  private hideResult(): void {
    if (this.resultOverlay) {
      this.resultOverlay.remove();
      this.resultOverlay = null;
    }
  }

  /**
   * Shows a toast notification.
   */
  showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  /**
   * Removes the HUD from the DOM.
   */
  destroy(): void {
    this.hideResult();
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
    this.inputEl = null;
    this.fireBtn = null;
    this.errorEl = null;
    this.turnIndicatorEl = null;
    this.team1ScoreEl = null;
    this.team2ScoreEl = null;
    this.botThinkingEl = null;
    this.historyListEl = null;
    this.callbacks = null;
  }
}
